import { USER_ACTIONS } from '../../../features/FormBuilder/data-layer/validation/validationRules';
import {
  type ActionDetailsPayload,
  type ActionPayload,
  type AiChatPayload,
  type ConfigSearchPayload,
  type CredentialsSearchPayload,
  type DataProviderPayload,
  type DuplicateCheckPreviewRequest,
  type DuplicateCheckRequest,
  type DuplicateCheckResponse,
  type DynamicAttributeNonUserPayload,
  type DynamicAttributePayload,
  type DynamicAttributeUserPayload,
  type EntitySearchPayload,
  type FormAccessPayload,
  type FormActionPayload,
  type FormApiResponse,
  type FormDuplicatePayload,
  type FormExportPayload,
  type FormPayload,
  type FormStatusTransitionRequest,
  type FormValidatePayload,
  type GetEntityDetailsPayload,
  type PreviewDataProviderPayload,
  type SearchPayload,
  type SubmitData,
  type SubmitPayload,
} from '../../types';
import {
  normalizeFormPayloadDatastoreInbound,
  withWireDatastoreFormPayload,
} from '../../utils/datastoreProviderWire';
import HttpServiceSingleton from '../HttpService/HttpService';

import {
  ACTIONS_LIST_GET,
  AI_CHAT_POST,
  BRANDING_UPLOAD_POST,
  CONFIG_SEARCH_POST,
  CREATE_FORM_POST,
  CREDENTIALS_SEARCH_POST,
  DA_ACTION,
  DA_REQUEST_TYPE,
  DATAPROVIDER_PREVIEW_POST,
  DATASTORE_OBJECTS_GET,
  FORM_ACCESS_BASE,
  FORM_CONFIG_GET,
  FORM_DUPLICATE_POST,
  FORM_ENTITIES_SEARCH_POST,
  FORM_EXPORT_POST,
  FORM_IMPORT_POST,
  FORM_STATUS_TRANSITION_PUT,
  FORM_VALIDATE_DUPLICATE_POST,
  FORM_VALIDATE_POST,
  FORMS_GET,
  FORMS_GET_CREATORS,
  FORMS_GET_STATUSES,
  FORMS_GET_UPDATERS,
  GENERATE_FORM_FROM_DA,
  GENERATE_FORM_FROM_DA_ACCOUNT,
  GENERATE_FORM_FROM_DA_SERVICE_ACCOUNT,
  GENERATE_FORM_FROM_DA_USER_CREATE,
  GENERATE_FORM_FROM_DA_USER_UPDATE,
  GET_ENTITY_DETAILS_POST,
  LIVE_FORMS_GET,
  ORCHESTRATION_INVOKE_METHOD_API,
  PREVIEW_FORM_VALIDATE_DUPLICATE_POST,
  PREVIEW_GET_ENTITY_DETAILS_POST,
  PUBLIC_FORM_SUBMIT,
  PUBLIC_FORM_VALIDATE_DUPLICATE_POST,
  PUBLIC_FORMS_GET,
  SEARCH_FORM_POST,
  SQLPROVIDER_DATA_POST,
  SUBMIT_FROM_POST,
  USER_ACTION_POST,
} from './constants';

export interface UsersProps {
  q: string;
  enabled?: boolean;
  size?: number;
  userType?: string;
  searchKeyword?: string;
  sortOrder?: string;
  page?: number;
  sortBy?: string;
}

export interface PublicSubmitPayload {
  urlSlug: string;
  customFormUid?: string;
  /** Captcha token sent at the top level of the payload, outside of `data`. */
  captchaToken?: string;
  data: SubmitData;
}

export interface PublicSubmitResponse {
  status: number;
  data?: {
    requestId?: string;
    requestKey?: string;
    message?: string;
    errorMessage?: string;
  };
  message?: string;
  errorMessage?: string;
}

export interface BrandingUploadResponse {
  status: number;
  message: string;
  data?: {
    base64Content: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    logoPath: string;
  };
}

/** Successful JSON POST envelope from HttpService.post when wrap is true (default). */
export interface HttpPostEnvelope<T = unknown> {
  status: number;
  data?: T;
}

const FormForgeApiService = {
  getForms(searchPayload: SearchPayload, signal?: AbortSignal) {
    return HttpServiceSingleton.post(
      `${SEARCH_FORM_POST}`,
      searchPayload,
      undefined,
      signal,
      true,
      false
    );
  },
  postForms(
    formPayload: FormPayload
  ): Promise<HttpPostEnvelope<FormApiResponse>> {
    return HttpServiceSingleton.post(
      `${CREATE_FORM_POST}`,
      withWireDatastoreFormPayload(formPayload)
    ) as Promise<HttpPostEnvelope<FormApiResponse>>;
  },
  uploadBrandingLogo(file: File): Promise<BrandingUploadResponse> {
    const formData = new FormData();

    formData.append('file', file);
    HttpServiceSingleton.deleteHeader('Content-Type');

    return HttpServiceSingleton.post(
      BRANDING_UPLOAD_POST,
      formData,
      undefined,
      undefined,
      false,
      false
    ) as Promise<BrandingUploadResponse>;
  },
  getFormsById(customFormUid: string, urlSlug: string): Promise<FormPayload> {
    return (
      HttpServiceSingleton.get(
        `${FORMS_GET}/${customFormUid}/${urlSlug}`
      ) as Promise<FormPayload>
    ).then(normalizeFormPayloadDatastoreInbound);
  },
  /** Authenticated live form GET (submit namespace), for LiveForm and similar. */
  getLiveFormById(
    customFormUid: string,
    urlSlug: string
  ): Promise<FormPayload> {
    return (
      HttpServiceSingleton.get(
        `${LIVE_FORMS_GET}/${customFormUid}/${urlSlug}`
      ) as Promise<FormPayload>
    ).then(normalizeFormPayloadDatastoreInbound);
  },
  /**
   * Legacy endpoint: fetches a form by urlSlug only (no customFormUid).
   * Used for backward compatibility when the host app navigates with the old URL format.
   */
  getFormsByUrlSlug(urlSlug: string): Promise<FormPayload> {
    return (
      HttpServiceSingleton.get(
        `${FORMS_GET}/${urlSlug}`
      ) as Promise<FormPayload>
    ).then(normalizeFormPayloadDatastoreInbound);
  },
  /** Legacy live GET by urlSlug only (submit namespace). */
  getLiveFormByUrlSlug(urlSlug: string): Promise<FormPayload> {
    return (
      HttpServiceSingleton.get(
        `${LIVE_FORMS_GET}/${urlSlug}`
      ) as Promise<FormPayload>
    ).then(normalizeFormPayloadDatastoreInbound);
  },
  getDatastoreObjects(signal?: AbortSignal): Promise<unknown> {
    return HttpServiceSingleton.get(DATASTORE_OBJECTS_GET, signal);
  },
  getDatastoreObjectDefinition(
    templateId: string,
    signal?: AbortSignal
  ): Promise<unknown> {
    const id = encodeURIComponent(templateId);

    return HttpServiceSingleton.get(
      `${DATASTORE_OBJECTS_GET}?objectKey=${id}`,
      signal
    );
  },
  /**
   * Fetches picker-backed dropdown options for a field in a datastore template.
   * Used when a template field has `pickerBacked: true`.
   */
  getDatastorePickerOptions(
    params: {
      objectKey: string;
      fieldAlias: string;
      searchText?: string;
      page?: number;
      pageSize?: number;
    },
    signal?: AbortSignal
  ): Promise<unknown> {
    const searchParams = new URLSearchParams();

    searchParams.set('objectKey', params.objectKey);
    searchParams.set('fieldAlias', params.fieldAlias);

    if (params.searchText) {
      searchParams.set('searchText', params.searchText);
    }

    searchParams.set('page', String(params.page ?? 0));
    searchParams.set('pageSize', String(params.pageSize ?? 10));

    return HttpServiceSingleton.get(
      `${DATASTORE_OBJECTS_GET}?${searchParams.toString()}`,
      signal
    );
  },
  getActionsList(payload: ActionPayload) {
    return HttpServiceSingleton.post(`${ACTIONS_LIST_GET}`, payload);
  },
  getCredentialsList(payload: CredentialsSearchPayload) {
    return HttpServiceSingleton.post(
      `${CREDENTIALS_SEARCH_POST}`,
      payload,
      undefined,
      undefined,
      true,
      false
    );
  },
  getActionDetails(actionDetailsPayload: ActionDetailsPayload) {
    return HttpServiceSingleton.post(
      `${USER_ACTION_POST}`,
      actionDetailsPayload
    );
  },
  putForms(formPayload: FormPayload): Promise<FormApiResponse> {
    return HttpServiceSingleton.put(
      `${CREATE_FORM_POST}/${formPayload.customFormUid ?? ''}`,
      withWireDatastoreFormPayload(formPayload)
    ) as Promise<FormApiResponse>;
  },
  deleteForms(id: string) {
    return HttpServiceSingleton.delete(`${CREATE_FORM_POST}/${id}`);
  },
  postSaveForm(formPayload: SubmitPayload) {
    const { token, ...payload } = formPayload;
    const url = token
      ? `${SUBMIT_FROM_POST}?token=${token}`
      : `${SUBMIT_FROM_POST}/${formPayload.customFormUid}/${formPayload.urlSlug}`;

    return HttpServiceSingleton.post(url, payload);
  },
  postDataProvider(providerPayload: DataProviderPayload, signal?: AbortSignal) {
    return HttpServiceSingleton.post(
      `${SQLPROVIDER_DATA_POST}`,
      providerPayload,
      undefined,
      signal
    );
  },
  postDataProviderPreview(
    providerPayload: PreviewDataProviderPayload,
    signal?: AbortSignal
  ) {
    return HttpServiceSingleton.post(
      `${DATAPROVIDER_PREVIEW_POST}`,
      providerPayload,
      undefined,
      signal
    );
  },
  postAiChat(payload: AiChatPayload) {
    return HttpServiceSingleton.post(`${AI_CHAT_POST}`, payload);
  },
  generateFormsByDynamicAttributes(
    payload: DynamicAttributePayload
  ): ReturnType<
    typeof HttpServiceSingleton.get | typeof HttpServiceSingleton.post
  > {
    const { requestType, action, endpointKey } = payload;

    switch (requestType) {
      case DA_REQUEST_TYPE.USERS: {
        const userPayload: DynamicAttributeUserPayload = { endpointKey };
        const url =
          action === DA_ACTION.UPDATE
            ? GENERATE_FORM_FROM_DA_USER_UPDATE
            : GENERATE_FORM_FROM_DA_USER_CREATE;

        return HttpServiceSingleton.post(url, userPayload);
      }

      case DA_REQUEST_TYPE.SERVICE_ACCOUNT: {
        const saPayload: DynamicAttributeNonUserPayload = {
          endpointKey,
          action,
        };

        return HttpServiceSingleton.post(
          GENERATE_FORM_FROM_DA_SERVICE_ACCOUNT,
          saPayload
        );
      }

      case DA_REQUEST_TYPE.ACCOUNT: {
        const accountPayload: DynamicAttributeNonUserPayload = {
          endpointKey,
          action,
        };

        return HttpServiceSingleton.post(
          GENERATE_FORM_FROM_DA_ACCOUNT,
          accountPayload
        );
      }

      default:
        return HttpServiceSingleton.post(`${GENERATE_FORM_FROM_DA}`, payload);
    }
  },
  formAction(action: string, payload: FormActionPayload) {
    return HttpServiceSingleton.post(`${FORMS_GET}/${action}`, payload);
  },
  getFormStatuses() {
    return HttpServiceSingleton.get(`${FORMS_GET_STATUSES}`);
  },
  getFormCreators(page = 0, size = 10) {
    return HttpServiceSingleton.post(`${FORMS_GET_CREATORS}`, {
      page,
      size,
      type: USER_ACTIONS.CREATE_USER,
    });
  },
  getFormUpdaters(page = 0, size = 10) {
    return HttpServiceSingleton.post(`${FORMS_GET_UPDATERS}`, {
      page,
      size,
      type: USER_ACTIONS.UPDATE_USER,
    });
  },
  exportForm(payload: FormExportPayload) {
    return HttpServiceSingleton.post(
      `${FORM_EXPORT_POST}`,
      payload,
      undefined,
      undefined,
      true,
      false
    );
  },
  importForm(payload: { data: FormPayload }) {
    return HttpServiceSingleton.post(`${FORM_IMPORT_POST}`, payload);
  },
  duplicateForm(payload: FormDuplicatePayload) {
    return HttpServiceSingleton.post(`${FORM_DUPLICATE_POST}`, payload);
  },
  validateForm(payload: FormValidatePayload) {
    return HttpServiceSingleton.post(
      `${FORM_VALIDATE_POST}`,
      payload,
      undefined,
      undefined,
      true,
      false
    );
  },
  putFormStatus(payload: FormStatusTransitionRequest) {
    return HttpServiceSingleton.put(`${FORM_STATUS_TRANSITION_PUT}`, payload);
  },
  postGetEntityDetails(payload: GetEntityDetailsPayload) {
    return HttpServiceSingleton.post(`${GET_ENTITY_DETAILS_POST}`, payload);
  },
  postGetEntityDetailsPreview(payload: GetEntityDetailsPayload) {
    return HttpServiceSingleton.post(
      `${PREVIEW_GET_ENTITY_DETAILS_POST}`,
      payload
    );
  },
  getOrchestrationInvokeMethods(actionEnum: string) {
    const url = ORCHESTRATION_INVOKE_METHOD_API.replace(':param', actionEnum);

    return HttpServiceSingleton.get(url);
  },
  getConfigSearch(payload: ConfigSearchPayload) {
    return HttpServiceSingleton.post(CONFIG_SEARCH_POST, payload);
  },
  // Form Access Management APIs
  getFormAccess(customFormUid: string) {
    return HttpServiceSingleton.get(`${FORM_ACCESS_BASE}/${customFormUid}`);
  },
  grantFormAccess(customFormUid: string, payload: FormAccessPayload) {
    return HttpServiceSingleton.post(
      `${FORM_ACCESS_BASE}/${customFormUid}`,
      payload
    );
  },
  searchFormEntities(payload: EntitySearchPayload) {
    return HttpServiceSingleton.post(FORM_ENTITIES_SEARCH_POST, payload);
  },

  // Public Form APIs (unauthenticated)
  /**
   * Fetches a public form by its ID (urlSlug).
   * This endpoint does not require authentication.
   * Note: Deletes Authorization header before request; HttpService resets headers after.
   *
   * @param customFormUid - The unique custom form UID
   * @param id - The form ID (urlSlug)
   * @param token - Authentication token for public form access
   * @param contextId - Context identifier for the form
   */
  getPublicFormById(
    customFormUid: string,
    id: string,
    token: string
  ): Promise<FormPayload> {
    HttpServiceSingleton.deleteHeader('Authorization');
    HttpServiceSingleton.setHeaders({ 'Saviynt-Form-Token': token });
    const url = `${PUBLIC_FORMS_GET}/${customFormUid}/${id}`;

    return (HttpServiceSingleton.get(url) as Promise<FormPayload>).then(
      normalizeFormPayloadDatastoreInbound
    );
  },

  /**
   * Submits form data for a public form.
   * This endpoint does not require authentication.
   * Note: Deletes Authorization header before request; HttpService resets headers after.
   */
  submitPublicForm(
    id: string,
    token: string,
    payload: PublicSubmitPayload
  ): Promise<PublicSubmitResponse> {
    HttpServiceSingleton.deleteHeader('Authorization');
    const url = `${PUBLIC_FORM_SUBMIT}/${payload.customFormUid}/${payload.urlSlug}`;

    return HttpServiceSingleton.post(url, {
      ...payload,
      token,
    }) as Promise<PublicSubmitResponse>;
  },

  // Duplicate Check Validation APIs
  validateDuplicateCheck(
    payload: DuplicateCheckRequest,
    signal?: AbortSignal
  ): Promise<DuplicateCheckResponse> {
    return HttpServiceSingleton.post(
      FORM_VALIDATE_DUPLICATE_POST,
      payload,
      undefined,
      signal,
      true,
      false
    ) as Promise<DuplicateCheckResponse>;
  },

  validatePublicDuplicateCheck(
    token: string,
    payload: DuplicateCheckRequest,
    signal?: AbortSignal
  ): Promise<DuplicateCheckResponse> {
    HttpServiceSingleton.deleteHeader('Authorization');
    const url = `${PUBLIC_FORM_VALIDATE_DUPLICATE_POST}?token=${encodeURIComponent(token)}`;

    return HttpServiceSingleton.post(
      url,
      payload,
      undefined,
      signal,
      true,
      false
    ) as Promise<DuplicateCheckResponse>;
  },

  validatePreviewDuplicateCheck(
    payload: DuplicateCheckPreviewRequest,
    signal?: AbortSignal
  ): Promise<DuplicateCheckResponse> {
    return HttpServiceSingleton.post(
      PREVIEW_FORM_VALIDATE_DUPLICATE_POST,
      payload,
      undefined,
      signal,
      true,
      false
    ) as Promise<DuplicateCheckResponse>;
  },

  // Form Configuration API
  getFormConfig() {
    return HttpServiceSingleton.get(FORM_CONFIG_GET);
  },
};

export default FormForgeApiService;
